// @ts-nocheck

import './PlaylistItemsList.scss';
import React, { useState, useEffect } from 'react';
import {arrayMoveImmutable} from 'array-move';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { playlistStore, generalStore } from '../../store';
import Loader from '../loader/Loader';
import PlaylistItem from '../playlistItem/PlaylistItem';
import Server from '../../js/config/_server';
import { RESPONSE, ROUTES } from '../../js/_constants';
import { apiDelete, apiPatch } from '../../js/api';
import DeleteConfirmationModal from '../deleteConfirmationModal/DeleteConfirmationModal';
import Helpers from '../../js/helpers';
import deleteIconPath from '@icons/trash.svg';
import addIconPath from '@icons/add-small.svg';
import duplicateIconPath from '@icons/duplicate.svg';
import { Link } from 'react-router';
import IconButton from '../iconButton/IconButton';
import {
    type Uuid,
    type PlaylistItem as DistributedMedium,
    type Playlist
} from '../../api/types/index';
import SortableList, { SortableItem } from "react-easy-sort";

type PlaylistItemsListProps = {
    setIsEditingPlaylist: any;
    loading: boolean;
    playlistId: Uuid;
    items: DistributedMedium[] | [];
    isEditable: boolean;
};

export const PlaylistItemsList = ({
    setIsEditingPlaylist,
    loading,
    playlistId,
    items,
    isEditable
}: PlaylistItemsListProps) => {
    const { t } = useTranslation();
    const { getPlaylistsRequest, getPlaylist, setSelectedPlaylist } = playlistStore();
    const { setAlert } = generalStore();

    const [isDeleteItemRequest, setIsDeleteItemRequest] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [playlistItems, setPlaylistItems] = useState(items);
    const [isSorting, setIsSorting] = useState(false);
    const [modalItem, setModalItem]: Playlist | any = useState({});

    useEffect(() => {
        return ()=> {
            setSelectedPlaylist({loading: true})
        }
    }, []);

    useEffect(() => {
        setPlaylistItems(items);
        const check = items.map(ele =>
            Helpers.getAssetLicenceStatus(ele)
        );
        if (check.includes('is-expired')) {
            setTimeout(() => {
                setAlert({
                    message: t('playlist_expired_alert'),
                    type: 'warning',
                    display: true,
                    isAutoDisappearing: true
                });
            }, 2000);
        }
    }, [items]);

    const handleCancelDeletion = () => {
        setIsDeleteItemRequest(false);
        setModalItem({});
    };

    const handleItemDeletion = async () => {
        setIsDeleting(true);
        const nextItems: DistributedMedium[] | [] = playlistItems.filter(
            item => item[RESPONSE.ID] !== modalItem[RESPONSE.ID]
        );
        const url = `${Server.baseUrl}${modalItem[RESPONSE.ID]}`;

        await apiDelete(url);
        await Promise.all(
            nextItems.map((item, i) => {
                const itemUrl = `${Server.baseUrl}${item[RESPONSE.ID]}`;
                const options = {
                    body: {
                        position: i + 1,
                        duration: item.duration
                    }
                };
                return apiPatch(itemUrl, options);
            })
        );

        setIsDeleteItemRequest(false);
        setModalItem({});
        setIsDeleting(false);
        setPlaylistItems(nextItems);
        await getPlaylist(playlistId, { isBackgroundRequest: true });
        await getPlaylistsRequest({ isBackgroundRequest: true });
    };

    const handleDeleteItemRequest = item => {
        setIsDeleteItemRequest(true);
        setModalItem(item);
    };

    if (loading) {
        return <Loader loading={loading} />;
    }

    const handleMove = (uuid, direction) => {   
        const position = playlistItems.findIndex((i) => i.uuid === uuid)
        if (position < 0) {
          throw new Error('Given item not found.')
        } else if (direction === -1 && position === 0 || direction === 1 && position === items.length - 1) {
          return // canot move outside of array
        }
    
        const item = playlistItems[position] // save item for later
        const newItems = playlistItems.filter((i) => i.uuid !== uuid) // remove item from array
        newItems.splice(position + direction, 0, item)
        setIsSorting(true);

        const promises = newItems.map((item, i) => {
            const url = `${Server.baseUrl}${item[RESPONSE.ID]}`;
            const options = {
                body: {
                    position: i + 1 
                }
            };
            return apiPatch(url, options);
        });
        Promise.all(promises).then(async () => {
            await getPlaylist(playlistId, {
                isBackgroundRequest: true
            });

            setIsSorting(false);
        });
      }


          const onSortEnd = async (oldIndex, newIndex) => {
              if (oldIndex !== newIndex) {
                setIsSorting(true);
      
                const newList = arrayMoveImmutable(playlistItems, oldIndex, newIndex)
                setPlaylistItems(newList);

                const promises = newList.map((item, i) => {
                    const url = `${Server.baseUrl}${item[RESPONSE.ID]}`;
                    const options = {
                        body: {
                            position: i + 1 
                        }
                    };
                    return apiPatch(url, options);
                });
    
                Promise.all(promises).then(async() => {
                setIsSorting(false);
                });
              }
          };

    return (
        <div className="b-playlist-items-list">
            <div className="b-playlist-items-list__list">
                {isSorting && <Loader />}
                {playlistItems.length === 0 ? (
                    <span className="b-playlist-items-list__empty-list-disclaimer">
                        {t('playlist_no_items')}
                    </span>
                ): 
                <SortableList onSortEnd={onSortEnd}>
                    {playlistItems.map((ele, index) => 
                        <SortableItem key={playlistId}>
                            <div>
                                <PlaylistItem 
                                    setIsEditingPlaylist={setIsEditingPlaylist}
                                    playlistId={playlistId}
                                    item={ele}
                                    deleteItemRequest={handleDeleteItemRequest}
                                    disabled={isSorting}
                                    isEditable={isEditable}
                                    onMove={handleMove}
                                    itemsLength={playlistItems.length}
                                    index={index}
                                /> 
                            </div>
                        </SortableItem>)
                    }
                </SortableList>}
            </div>
            <DeleteConfirmationModal
                show={isDeleteItemRequest}
                onClose={handleCancelDeletion}
                onCancel={handleCancelDeletion}
                onAction={handleItemDeletion}
                localeAction={t('delete')}
                localeCancel={t('cancel')}
                isLoading={isDeleting}
            >
                <h4>{t('delete_playlist_item')}</h4>
            </DeleteConfirmationModal>
        </div>
    );
};

export default PlaylistItemsList;
